/**
 * Typed command surface. Every mutating call carries an idempotency key, and
 * every response carries a resource version plus the audit event it produced.
 */
import { z } from 'zod';
import { AGENT_STATUSES, SESSION_STATUSES, WORK_ITEM_STATUSES } from './states.js';
// Zod needs a non-empty tuple; the state contract owns the values themselves.
const AGENT_STATUS_VALUES = AGENT_STATUSES;
const SESSION_STATUS_VALUES = SESSION_STATUSES;
const WORK_ITEM_STATUS_VALUES = WORK_ITEM_STATUSES;
export const ROLES = [
    'tenant_admin',
    'integration_admin',
    'project_owner',
    'operator',
    'contributor',
    'viewer',
];
/** Least-privilege matrix: which role may perform which action class. */
export const ROLE_CAPABILITIES = {
    tenant_admin: ['*'],
    integration_admin: [
        'connection:read',
        'connection:write',
        'credential:rotate',
        'project:read',
        'audit:read',
    ],
    project_owner: [
        'project:read',
        'project:write',
        'decision:resolve',
        'blocker:resolve',
        'station:approve',
        'lease:takeover',
        'handoff:create',
        'connection:read',
        'audit:read',
    ],
    operator: [
        'project:read',
        'lease:acquire',
        'lease:renew',
        'lease:release',
        'dispatch:write',
        'decision:resolve',
        'blocker:resolve',
        'handoff:create',
        'connection:read',
    ],
    contributor: ['project:read', 'blocker:resolve', 'connection:read'],
    viewer: ['project:read', 'connection:read', 'audit:read'],
};
export function roleAllows(role, capability) {
    const caps = ROLE_CAPABILITIES[role];
    return caps.includes('*') || caps.includes(capability);
}
export const loginRequestSchema = z
    .object({ email: z.string().email(), password: z.string().min(1) })
    .strict();
export const leaseAcquireSchema = z
    .object({
    workstream: z.string().min(1).max(64),
    ownerSessionId: z.string().min(1),
    ttlSeconds: z.number().int().min(10).max(300).optional(),
    renewEverySeconds: z.number().int().min(2).max(120).optional(),
})
    .strict();
export const leaseRenewSchema = z
    .object({
    workstream: z.string().min(1).max(64),
    ownerSessionId: z.string().min(1),
    fencingToken: z.number().int().positive(),
})
    .strict();
export const leaseReleaseSchema = z
    .object({
    workstream: z.string().min(1).max(64),
    ownerSessionId: z.string().min(1),
    fencingToken: z.number().int().positive(),
})
    .strict();
export const leaseTakeoverSchema = z
    .object({
    workstream: z.string().min(1).max(64),
    ownerSessionId: z.string().min(1),
    reason: z.string().min(8).max(500),
    force: z.boolean().optional(),
})
    .strict();
export const dispatchSchema = z
    .object({
    workstream: z.string().min(1).max(64),
    workItemId: z.string().min(1),
    agentId: z.string().min(1),
    fencingToken: z.number().int().positive(),
})
    .strict();
export const decisionResolveSchema = z
    .object({
    optionKey: z.string().min(1),
    reason: z.string().min(3).max(1000),
    expectedVersion: z.number().int().nonnegative().optional(),
})
    .strict();
export const blockerResolveSchema = z
    .object({
    resolution: z.enum(['RESOLVED', 'MITIGATED']),
    reason: z.string().min(3).max(1000),
    expectedVersion: z.number().int().nonnegative().optional(),
})
    .strict();
export const stationApprovalSchema = z
    .object({
    decision: z.enum(['APPROVED', 'REJECTED', 'DEFERRED']),
    reason: z.string().min(3).max(1000),
    expectedVersion: z.number().int().nonnegative().optional(),
})
    .strict();
export const handoffCreateSchema = z
    .object({
    projectId: z.string().min(1),
    workstream: z.string().min(1),
    toMemberId: z.string().min(1),
    reason: z.string().min(3).max(500),
})
    .strict();
export const manifestValidateSchema = z.object({ manifest: z.string().min(1) }).strict();
export const discoverSchema = z
    .object({
    provider: z.literal('github'),
    installationId: z.string().min(1),
    repositories: z.array(z.string().min(1)).min(1).max(20),
})
    .strict();
export const confirmMappingSchema = z
    .object({
    mappings: z
        .array(z.object({
        detectedKey: z.string().min(1),
        entityType: z.enum(['service', 'environment', 'repository', 'source_of_truth']),
        confirmedValue: z.string().min(1),
        accepted: z.boolean(),
    }))
        .min(1),
    reason: z.string().max(500).optional(),
})
    .strict();
export const capacityObservationSchema = z
    .object({
    scopeType: z.enum(['tenant', 'member', 'seat', 'project']),
    scopeId: z.string().min(1),
    layer: z.enum(['session_range', 'weekly_fuel', 'seat_plan']),
    usedPercent: z.number().min(0).max(100),
    quality: z.enum(['DIRECT', 'REPORTED', 'DERIVED', 'FIXTURE', 'UNKNOWN']),
    observedAt: z.string().min(1),
    resetsAt: z.string().min(1).optional(),
    activityClass: z
        .enum([
        'PRODUCTIVE',
        'ACTIVE_UNVERIFIED',
        'IDLE_NO_WORK',
        'WAITING_DEPENDENCY',
        'WAITING_HUMAN',
        'BLOCKED_LIMIT',
        'BLOCKED_ERROR',
        'PAUSED_POLICY',
        'OFFLINE',
        'UNKNOWN',
    ])
        .optional(),
    minutes: z.number().min(0).max(10080).optional(),
})
    .strict();
export const heartbeatSchema = z
    .object({
    scope: z.enum(['connection', 'session', 'agent']),
    id: z.string().min(1),
    at: z.string().min(1).optional(),
})
    .strict();
/* ------------------------------------------------------------------ */
/* Agent reporting                                                      */
/* ------------------------------------------------------------------ */
/**
 * What an agent may tell DCC about its own work.
 *
 * Deliberately a closed union rather than a free-form event envelope. An agent
 * is an untrusted reporter: it should be able to say "I am blocked on this"
 * without also being able to invent a build, a deployment or a capacity
 * measurement it has no way of observing.
 *
 * Every report is self-reported by definition, so none of these carries a
 * provenance field. The server stamps them REPORTED and refuses any attempt to
 * claim more.
 */
const agentIdentitySchema = z
    .object({
    /** Stable id for this agent across restarts. */
    id: z.string().min(1).max(120),
    displayName: z.string().min(1).max(160).optional(),
    /** Provider session the agent is running inside. */
    sessionId: z.string().min(1).max(200),
    workstream: z.string().min(1).max(120).optional(),
})
    .strict();
const agentReportBase = {
    /** Project slug or id. Checked against the key's binding, never trusted. */
    project: z.string().min(1).max(120),
    at: z.string().min(1).optional(),
    /** Caller-chosen id making the report idempotent across retries. */
    reportId: z.string().min(1).max(200),
};
export const agentReportSchema = z.discriminatedUnion('kind', [
    z
        .object({
        ...agentReportBase,
        kind: z.literal('agent.status'),
        agent: agentIdentitySchema,
        status: z.enum(AGENT_STATUS_VALUES),
        detail: z.string().max(2000).optional(),
        workItemKey: z.string().max(120).optional(),
        branch: z.string().max(200).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('assignment'),
        agent: agentIdentitySchema,
        workItemKey: z.string().min(1).max(120),
        detail: z.string().max(2000).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('progress'),
        agent: agentIdentitySchema.optional(),
        workItemKey: z.string().min(1).max(120),
        status: z.enum(WORK_ITEM_STATUS_VALUES),
        reason: z.string().max(2000).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('blocker.opened'),
        blockerKey: z.string().min(1).max(120),
        title: z.string().min(1).max(300),
        severity: z.enum(['INFO', 'ATTENTION', 'RISK', 'BLOCKER', 'CRITICAL']),
        cause: z.string().min(1).max(2000),
        impact: z.string().max(2000).optional(),
        recommendedAction: z.string().max(2000).optional(),
        workItemKey: z.string().max(120).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('blocker.resolved'),
        blockerKey: z.string().min(1).max(120),
        resolution: z.string().min(1).max(2000),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('decision.required'),
        decisionKey: z.string().min(1).max(120),
        question: z.string().min(1).max(500),
        context: z.string().max(4000).optional(),
        options: z
            .array(z
            .object({
            key: z.string().min(1).max(80),
            label: z.string().min(1).max(200),
            consequence: z.string().max(1000),
        })
            .strict())
            .min(2)
            .max(8),
        severity: z.enum(['INFO', 'ATTENTION', 'RISK', 'BLOCKER', 'CRITICAL']),
        requiredBy: z.string().max(60).optional(),
        workItemKey: z.string().max(120).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('heartbeat'),
        agent: agentIdentitySchema,
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('session.state'),
        sessionId: z.string().min(1).max(200),
        /** Required the first time a session is seen, so DCC knows whose it is. */
        seatId: z.string().max(120).optional(),
        status: z.enum(SESSION_STATUS_VALUES),
        resetsAt: z.string().max(60).optional(),
        windowStart: z.string().max(60).optional(),
        windowEnd: z.string().max(60).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.literal('lease.acquire'),
        workstream: z.string().min(1).max(120),
        ownerSessionId: z.string().min(1).max(200),
        ttlSeconds: z.number().int().min(30).max(3600).optional(),
        renewEverySeconds: z.number().int().min(10).max(1800).optional(),
        agentId: z.string().max(120).optional(),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.enum(['lease.renew', 'lease.release']),
        workstream: z.string().min(1).max(120),
        ownerSessionId: z.string().min(1).max(200),
        /** Proof the holder is who it was when the lease was granted. */
        fencingToken: z.number().int().min(1),
    })
        .strict(),
    z
        .object({
        ...agentReportBase,
        kind: z.enum(['run.started', 'run.stopped', 'run.completed']),
        agent: agentIdentitySchema,
        workItemKey: z.string().max(120).optional(),
        detail: z.string().max(2000).optional(),
        /** Only meaningful on run.completed. */
        outcome: z.enum(['SUCCEEDED', 'FAILED', 'ABANDONED']).optional(),
    })
        .strict(),
]);
export const agentReportRequestSchema = z
    .object({ reports: z.array(agentReportSchema).min(1).max(100) })
    .strict();
//# sourceMappingURL=api.js.map